import {
  ExclamationCircleFilled,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SettingOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { Button, Dropdown, Form, InputNumber, Menu, Modal, Space, Table } from "antd";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Footer from "../../../components/Footer";
import NavBar from "../../../components/NavBar";
import SideBar from "../../../components/SideBar";
import AddAvenant from "./AddAvenant";
import axiosInstance from "../../../variables/api/axiosInstance";
import DetailsAvenant from "./DetailsAvenant";

const Avenants = () => {
  const [avenants, setAvenants] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [showModal, setShowModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedAvenant, setSelectedAvenant] = useState(null);
  const [editable, setEditable] = useState(false);
  const [editingKey, setEditingKey] = useState('');
  const [categories, setCategories] = useState([]);

  const [form] = Form.useForm();

  const edit = (record) => {
    form.setFieldsValue({
      ...record,
      // Map accessories to a key-value object for the form
      accessoires: record.accessoires.reduce((acc, curr) => {
        acc[curr.id] = curr.value;
        return acc;
      }, {}),
    });
    setEditingKey(record.avenantId);
  };


  const fetchAvenants = async () => {
    try {
      const res = await axiosInstance.get(`/api/avenant`);
      setAvenants(res.data.reverse()); // pour afficher les plus récents en haut
    } catch (err) {
      console.error(err);
      toast.error("Erreur lors du chargement des avenants");
    }
  };

  useEffect(() => {
    fetchAvenants();

    axiosInstance
      .get("/api/categorie")
      .then((res) => {
        setCategories(res.data);
      })
      .catch((err) => {
        console.log(err);
      });
  }, [showModal]);

  const showDeleteConfirm = (avenantId) => {
    Modal.confirm({
      title: "Êtes-vous sûr de vouloir supprimer cet avenant ?",
      icon: <ExclamationCircleFilled />,
      content: "Cette action est irréversible.",
      okText: "Oui",
      okType: "danger",
      cancelText: "Annuler",
      onOk() {
        deleteAvenant(avenantId);
      },
    });
  };

  const deleteAvenant = async (avenantId) => {
    try {
      await axiosInstance.delete(`/api/avenant/${avenantId}`);
      setAvenants((prev) => prev.filter((av) => av.avenantId !== avenantId));
      toast.success("Avenant supprimé avec succès");
    } catch (err) {
      console.error(err);
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleShow = (edit, avenant) => {
    setEditMode(edit);
    setSelectedAvenant(avenant);
    setShowModal(true);
  };

  const handleShowDetails = (avenant) => {
    setSelectedAvenant(avenant);
    setShowDetailsModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setSelectedAvenant(null);
  };

  const handleDetailsClose = () => {
    setSelectedAvenant(null);
    setShowDetailsModal(false);
  };

  const handleAddSuccess = (newAvenant) => {
    setAvenants((prev) => [newAvenant, ...prev]);
    setShowModal(false);
  };

  const paginatedData = avenants.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  // Définition des colonnes du tableau
  const columns = [
    {
      title: 'Nom',
      dataIndex: 'nom',
      key: 'nom',
    },
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Pénalité',
      dataIndex: 'penalite',
      key: 'penalite',
      render: (penalite) => penalite ?? 1,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => {
        const menu = (
          <Menu>
            <Menu.Item key="details" onClick={() => handleShowDetails(record)}>
              <FileTextOutlined /> Détails
            </Menu.Item>
            <Menu.Item key="edit" onClick={() => handleShow(true, record)}>
              <EditOutlined /> Modifier
            </Menu.Item>
            <Menu.Item key="delete" onClick={() => showDeleteConfirm(record.avenantId)} danger>
              <DeleteOutlined /> Supprimer
            </Menu.Item>
          </Menu>
        );

        return (
          <Dropdown overlay={menu} trigger={['click']}>
            <Button icon={<SettingOutlined />} />
          </Dropdown>
        );
      },
    },
  ];

  const expandedRowRender = (record) => {
    // Map accessories to a list of Form.Item components
    const formItems = record.accessoires.map(acc => (
      <Form.Item
        key={acc.id}
        name={['accessoires', acc.id]}
        label={`Montant ${acc.category}`}
        rules={[{ required: true, message: 'Please enter a value' }]}
      >
        <InputNumber
          style={{ width: 200 }}
          disabled={!editable}
          formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
          parser={(value) => value.replace(/\s?|(,*)/g, '')}
        />
      </Form.Item>
    ));

    return (
      <Form form={form} component={false}>
        <Space direction="vertical" style={{ width: '100%', padding: '16px' }}>
          {formItems}
        </Space>
      </Form>
    );
  };


  return (
    <div className="layout-wrapper layout-content-navbar">
      <div className="layout-container">
        <SideBar />
        <div className="layout-page">
          <NavBar />
          <div className="content-wrapper">
            <div className="container-xxl flex-grow-1 container-p-y">
              <Button
                type="primary"
                icon={<PlusOutlined />}
                style={{ marginBottom: 16 }}
                onClick={() => handleShow(false, null)}
              >
                Ajouter un nouvel avenant
              </Button>
              <div className="card">
                <h5 className="card-header">Les différents avenants</h5>
                <div className="table-responsive text-nowrap">
                  <Table
                    columns={columns}
                    dataSource={avenants}
                    rowKey="avenantId" // C'est crucial pour la performance et le suivi des lignes
                    expandedRowRender={expandedRowRender}
                    // Add expand icon controlled by edit state
                    expandable={{
                      expandedRowKeys: [editingKey],
                      // Add a custom expand icon to prevent it from appearing in view mode
                      expandIcon: ({ expanded, onExpand, record }) => {
                        return editable ? (
                          <Button type="link" onClick={e => onExpand(record, e)}>{expanded ? 'Collapse' : 'Expand'}</Button>
                        ) : null;
                      }
                    }}
                    pagination={{ showSizeChanger: true }}
                  />
                </div>
                {/* <div className="d-flex justify-content-end mt-3 me-3">
                  <Pagination
                    current={currentPage}
                    pageSize={pageSize}
                    total={avenants.length}
                    onChange={(page) => setCurrentPage(page)}
                    showSizeChanger={false}
                  />
                </div> */}
              </div>
            </div>
            <Footer />
          </div>
        </div>
      </div>
      <AddAvenant
        show={showModal}
        handleClose={handleClose}
        editMode={editMode}
        selectedAvenant={selectedAvenant}
        onAddSuccess={handleAddSuccess}
      />
      <DetailsAvenant
        open={showDetailsModal}
        setOpen={handleDetailsClose}
        avenant={selectedAvenant}
        categories={categories}
      />
    </div>
  );
};

export default Avenants;
